import { useEffect, useRef } from 'react';

import { useAvatarNotification } from 'features/users/hooks/useAvatarNotification';
import { useUser } from 'features/users/hooks/useUser';

import { useCurrentUser } from 'shared/hooks/useCurrentUser';

/**
 * A profile picture upload is asynchronous and out of band: saveProfilePicture pushes the
 * file to storage, and the resulting profilePicId only reaches the client through a SignalR
 * `newFile` notification (see useAvatarNotification). Anywhere the picture is *required* we
 * have to wait for that round trip, or the API rejects a request the user legitimately
 * completed.
 */
export const PROFILE_PICTURE_CONFIRM_TIMEOUT = 20000; // milliseconds

interface UploadablePicture {
  name?: string;
  url?: string;
  file?: File;
  removePicture?: boolean;
}

export const useProfilePictureUpload = (): {
  uploadAndConfirm: (profilePic?: UploadablePicture) => Promise<boolean>;
} => {
  const { currentUser } = useCurrentUser();
  const { saveProfilePicture } = useUser(currentUser.id);

  // The confirmation this hook waits on only exists if something is subscribed to the SignalR
  // `newFile` push. That listener used to live on CreatorProfile and BrandUserAccountSettings
  // only, so during onboarding the upload succeeded, nothing applied profilePicId, and the wait
  // below timed out. Subscribing here keeps the listener and the waiter together.
  useAvatarNotification();

  // Resolver for an in-flight upload, settled by the effect below when the id lands.
  const pendingConfirmation = useRef<((confirmed: boolean) => void) | null>(null);

  useEffect(() => {
    if (currentUser.profilePicId && pendingConfirmation.current) {
      pendingConfirmation.current(true);
      pendingConfirmation.current = null;
    }
  }, [currentUser.profilePicId]);

  useEffect(
    () => () => {
      // Unmounting mid-upload must not leave the caller awaiting forever.
      pendingConfirmation.current?.(false);
      pendingConfirmation.current = null;
    },
    [],
  );

  const uploadAndConfirm = async (profilePic?: UploadablePicture): Promise<boolean> => {
    if (currentUser.profilePicId) return true;
    if (!profilePic?.file) return false;

    const confirmed = new Promise<boolean>((resolve) => {
      pendingConfirmation.current = resolve;
    });
    const timedOut = new Promise<boolean>((resolve) => {
      setTimeout(() => resolve(false), PROFILE_PICTURE_CONFIRM_TIMEOUT);
    });

    await saveProfilePicture(profilePic);

    return Promise.race([confirmed, timedOut]);
  };

  return { uploadAndConfirm };
};
